/**
 * Meta (Facebook / Instagram) ad-funnel events: one list for the web Pixel
 * (client/src/lib/metaPixel.ts) and the app SDK (mobile/lib/analytics.ts), so
 * web and app campaigns report the same mid-funnel steps under the same names
 * in Events Manager and Ads Manager.
 *
 * Sign-up, checkout and purchase go to Meta as STANDARD events
 * (CompleteRegistration / InitiateCheckout / Purchase) from each platform's
 * own code; everything listed here is a custom event, named exactly like the
 * GA4 / PostHog event it mirrors.
 */
export const META_FUNNEL_CUSTOM_EVENTS = [
  "generate_song",
  "song_created",
  "try_to_generate_video",
  "activation_video",
  "try_to_generate_lyric_video",
  "video_project_initiated",
  "music_analysis_complete",
  "visual_style_complete",
  "creative_brief_complete",
  "references_complete",
  "shot_list_complete",
  "storyboard_complete",
  "render_complete",
  "video_project_completed",
  "subscription_purchased",
  "credit_topup_purchased",
] as const;

export type MetaFunnelCustomEvent = (typeof META_FUNNEL_CUSTOM_EVENTS)[number];

const META_FUNNEL_CUSTOM_EVENT_SET: ReadonlySet<string> = new Set(
  META_FUNNEL_CUSTOM_EVENTS,
);

export function isMetaFunnelCustomEvent(
  event: string,
): event is MetaFunnelCustomEvent {
  return META_FUNNEL_CUSTOM_EVENT_SET.has(event);
}

/**
 * The mobile Studio stepper's steps (mobile/app/studio-stepper.tsx StepId)
 * mapped to the web Director funnel event for the same milestone
 * (client/src/lib/dataLayer.ts VIDEO_PROJECT_FUNNEL_STEP_EVENTS). The stepper
 * has no visual-style step. "export" is the finished video.
 */
export const DIRECTOR_STEP_META_EVENTS = {
  analyze: "music_analysis_complete",
  brief: "creative_brief_complete",
  references: "references_complete",
  shotlist: "shot_list_complete",
  storyboard: "storyboard_complete",
  render: "render_complete",
  export: "video_project_completed",
} as const satisfies Record<string, MetaFunnelCustomEvent>;

export type DirectorFunnelStep = keyof typeof DIRECTOR_STEP_META_EVENTS;

const DIRECTOR_FUNNEL_STEPS = Object.keys(
  DIRECTOR_STEP_META_EVENTS,
) as DirectorFunnelStep[];

/**
 * The step-completion events to send for one stepper state.
 *
 * Each project+step is sent at most once per app run: `claimed` is a
 * module-level set the caller keeps across mounts, so a remount loop or a
 * done flag that flickers while the server run hands off to local state
 * never repeats an event. With `baseline` (the project's first load on this
 * mount), steps that are already done are claimed WITHOUT being returned:
 * they finished in an earlier session, and reopening a finished project must
 * not report its whole funnel again.
 */
export function claimDirectorFunnelEvents(
  projectId: string | null | undefined,
  stepDone: Partial<Record<string, boolean>>,
  claimed: Set<string>,
  options: { baseline: boolean },
): MetaFunnelCustomEvent[] {
  if (!projectId) return [];
  const events: MetaFunnelCustomEvent[] = [];
  for (const step of DIRECTOR_FUNNEL_STEPS) {
    if (!stepDone[step]) continue;
    const key = `${projectId}:${step}`;
    if (claimed.has(key)) continue;
    claimed.add(key);
    if (!options.baseline) events.push(DIRECTOR_STEP_META_EVENTS[step]);
  }
  return events;
}
